import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import {
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import {
  getPublicScheduleAvailability,
  getPublicProfessionals,
  IPublicAvailabilityInterval,
  IPublicProfessional,
  postPublicScheduleRequest,
} from '../../http';
import {
  PublicScheduleContainer,
  PublicScheduleFormCard,
  PublicScheduleHero,
  PublicScheduleHighlights,
  PublicScheduleMetrics,
  PublicScheduleShell,
  PublicScheduleSummary,
  SuccessCard,
  SuccessGrid,
} from './styles';
import { useI18n } from '../../shared/i18n';

interface ISubmittedSchedule {
  id: string;
  patient_name: string;
  professional_name: string;
  start: string;
  end: string;
  status: string;
}

const timeOptions = Array.from({ length: 21 }, (_, index) => {
  const totalMinutes = 8 * 60 + index * 30;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const minutes = String(totalMinutes % 60).padStart(2, '0');
  const value = `${hours}:${minutes}`;

  return {
    label: value,
    value,
  };
});

const addMinutesToTime = (time: string, minutesToAdd: number) => {
  const [hours, minutes] = time.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + minutesToAdd;
  const nextHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const nextMinutes = String(totalMinutes % 60).padStart(2, '0');
  return `${nextHours}:${nextMinutes}`;
};

const normalizeDigits = (value?: string) => value?.replace(/\D/g, '') || '';

const formatCellPhoneMask = (value?: string) => {
  const digits = normalizeDigits(value).slice(0, 11);

  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const overlapsInterval = (
  startTime: string,
  endTime: string,
  busyIntervals: IPublicAvailabilityInterval[],
) => {
  const startValue = dayjs(`2000-01-01T${startTime}:00`);
  const endValue = dayjs(`2000-01-01T${endTime}:00`);

  return busyIntervals.some((interval) => {
    const intervalStart = dayjs(interval.start);
    const intervalEnd = dayjs(interval.end);
    const compareStart = dayjs(
      `2000-01-01T${intervalStart.format('HH:mm')}:00`,
    );
    const compareEnd = dayjs(`2000-01-01T${intervalEnd.format('HH:mm')}:00`);

    return startValue.isBefore(compareEnd) && endValue.isAfter(compareStart);
  });
};

const PublicSchedule: React.FC = () => {
  const { tenant } = useParams();
  const [form] = Form.useForm();
  const { addToast } = useToast();
  const { t, language } = useI18n();

  const [loadingProfessionals, setLoadingProfessionals] =
    useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [professionals, setProfessionals] = useState<IPublicProfessional[]>([]);
  const [busyIntervals, setBusyIntervals] = useState<
    IPublicAvailabilityInterval[]
  >([]);
  const [loadingAvailability, setLoadingAvailability] =
    useState<boolean>(false);
  const [submittedSchedule, setSubmittedSchedule] =
    useState<ISubmittedSchedule | null>(null);
  const [loadedClinic, setLoadedClinic] = useState<string | null>(
    tenant || null,
  );

  const selectedProfessionalId = Form.useWatch('professional_id', form);
  const selectedDate = Form.useWatch('date', form);
  const selectedDuration = Form.useWatch('duration_minutes', form) || 30;
  const selectedStartTime = Form.useWatch('start_time', form);

  const selectedProfessional = professionals.find(
    (professional) => professional.id === selectedProfessionalId,
  );
  const computedEndTime = selectedStartTime
    ? addMinutesToTime(selectedStartTime, selectedDuration)
    : null;

  const durationOptions = [
    { label: t('publicSchedule.durationOptions.minutes30'), value: 30 },
    { label: t('publicSchedule.durationOptions.minutes60'), value: 60 },
  ];

  const formatShortDate = (value: dayjs.Dayjs | string) => {
    const dateValue =
      typeof value === 'string' ? new Date(value) : value.toDate();

    return new Intl.DateTimeFormat(language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(dateValue);
  };

  const timeOptionsWithAvailability = timeOptions.map((option) => {
    const endTime = addMinutesToTime(option.value, selectedDuration);
    const disabled = overlapsInterval(option.value, endTime, busyIntervals);

    return {
      ...option,
      disabled,
      label: disabled
        ? `${option.label} · ${t('publicSchedule.availability.unavailableSuffix')}`
        : option.label,
    };
  });

  const availableSlotsCount = timeOptionsWithAvailability.filter(
    (option) => !option.disabled,
  ).length;

  const loadAvailability = useCallback(
    async (professionalId: string, dateValue: string) => {
      try {
        setLoadingAvailability(true);
        const response = await getPublicScheduleAvailability({
          professional_id: professionalId,
          date: dateValue,
        });

        setBusyIntervals(response.busy_intervals || []);
      } catch (error: any) {
        setBusyIntervals([]);
        addToast({
          type: 'error',
          title: t('publicSchedule.availability.errorTitle'),
          description:
            error.response?.data?.message ||
            t('publicSchedule.availability.errorDescription'),
        });
      } finally {
        setLoadingAvailability(false);
      }
    },
    [addToast, t],
  );

  const loadClinicContext = useCallback(
    async (clinicCode: string) => {
      const normalizedClinic = clinicCode.trim();

      if (!normalizedClinic) {
        return;
      }

      try {
        setLoadingProfessionals(true);
        sessionStorage.setItem('@Sigeclin:clinic', normalizedClinic);
        api.defaults.headers['x-tenant-id'] = normalizedClinic;

        const response = await getPublicProfessionals();

        setProfessionals(response);
        setLoadedClinic(normalizedClinic);
      } catch (error: any) {
        setProfessionals([]);
        setLoadedClinic(null);
        addToast({
          type: 'error',
          title: t('publicSchedule.clinic.loadErrorTitle'),
          description:
            error.response?.data?.message ||
            t('publicSchedule.clinic.loadErrorDescription'),
        });
      } finally {
        setLoadingProfessionals(false);
      }
    },
    [addToast, t],
  );

  useEffect(() => {
    if (tenant) {
      form.setFieldValue('clinic', tenant);
      loadClinicContext(tenant);
    }
  }, [tenant, form, loadClinicContext]);

  useEffect(() => {
    if (!selectedProfessionalId || !selectedDate || !loadedClinic) {
      setBusyIntervals([]);
      return;
    }

    loadAvailability(selectedProfessionalId, selectedDate.format('YYYY-MM-DD'));
  }, [selectedProfessionalId, selectedDate, loadedClinic, loadAvailability]);

  useEffect(() => {
    if (!selectedStartTime) {
      return;
    }

    const endTime = addMinutesToTime(selectedStartTime, selectedDuration);
    const hasConflict = overlapsInterval(
      selectedStartTime,
      endTime,
      busyIntervals,
    );

    if (hasConflict) {
      form.setFieldValue('start_time', undefined);
      addToast({
        type: 'warning',
        title: t('publicSchedule.warnings.slotUnavailableTitle'),
        description: t('publicSchedule.warnings.slotUnavailableDescription'),
      });
    }
  }, [selectedDuration, selectedStartTime, busyIntervals, form, addToast, t]);

  const handleLoadClinic = useCallback(async () => {
    const clinicCode = form.getFieldValue('clinic');

    if (!clinicCode) {
      addToast({
        type: 'warning',
        title: t('publicSchedule.clinic.missingTitle'),
        description: t('publicSchedule.clinic.missingDescription'),
      });
      return;
    }

    await loadClinicContext(clinicCode);
  }, [form, loadClinicContext, addToast, t]);

  const handleSubmit = useCallback(
    async (values: any) => {
      const clinicCode = tenant || values.clinic;

      if (!clinicCode) {
        addToast({
          type: 'warning',
          title: t('publicSchedule.clinic.unavailableTitle'),
          description: t('publicSchedule.clinic.unavailableDescription'),
        });
        return;
      }

      const endTime = addMinutesToTime(
        values.start_time,
        values.duration_minutes,
      );

      if (overlapsInterval(values.start_time, endTime, busyIntervals)) {
        addToast({
          type: 'warning',
          title: t('publicSchedule.warnings.slotUnavailableTitle'),
          description: t('publicSchedule.warnings.slotOccupiedDescription'),
        });
        return;
      }

      try {
        setSubmitting(true);
        sessionStorage.setItem('@Sigeclin:clinic', clinicCode);
        api.defaults.headers['x-tenant-id'] = clinicCode;

        const response = await postPublicScheduleRequest({
          patient_name: values.patient_name.trim(),
          cpf_cnpj: normalizeDigits(values.cpf_cnpj),
          birthday: values.birthday.format('YYYY-MM-DD'),
          cell_phone: normalizeDigits(values.cell_phone),
          email: values.email?.trim() || undefined,
          professional_id: values.professional_id,
          date: values.date.format('YYYY-MM-DD'),
          start_time: values.start_time,
          end_time: endTime,
          observation: values.observation?.trim() || undefined,
        });

        setSubmittedSchedule(response.data);
        addToast({
          type: 'success',
          title: t('publicSchedule.submit.successTitle'),
          description: t('publicSchedule.submit.successDescription'),
        });
      } catch (error: any) {
        addToast({
          type: 'error',
          title: t('publicSchedule.submit.errorTitle'),
          description:
            error.response?.data?.message ||
            error.response?.data?.error ||
            t('publicSchedule.submit.errorDescription'),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [tenant, addToast, t],
  );

  return (
    <PublicScheduleContainer>
      <PublicScheduleShell>
        <PublicScheduleHero>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            <span className="hero-badge">{t('publicSchedule.hero.badge')}</span>
            <LanguageSwitcher compact />
          </div>
          <Typography.Title level={1}>
            {t('publicSchedule.hero.title')}
          </Typography.Title>
          <Typography.Paragraph style={{ color: '#fff' }}>
            {t('publicSchedule.hero.description')}
          </Typography.Paragraph>

          <PublicScheduleHighlights>
            <div className="highlight-item">
              <span className="highlight-label">
                {t('publicSchedule.highlights.flowLabel')}
              </span>
              <span className="highlight-value">
                {t('publicSchedule.highlights.flowValue')}
              </span>
            </div>
            <div className="highlight-item">
              <span className="highlight-label">
                {t('publicSchedule.highlights.integrationLabel')}
              </span>
              <span className="highlight-value">
                {t('publicSchedule.highlights.integrationValue')}
              </span>
            </div>
            <div className="highlight-item">
              <span className="highlight-label">
                {t('publicSchedule.highlights.statusLabel')}
              </span>
              <span className="highlight-value">
                {t('publicSchedule.highlights.statusValue')}
              </span>
            </div>
          </PublicScheduleHighlights>

          <PublicScheduleMetrics>
            <div className="metric-card">
              <span className="metric-label">
                {t('publicSchedule.metrics.professionals')}
              </span>
              <span className="metric-value">{professionals.length}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">
                {t('publicSchedule.metrics.availableSlots')}
              </span>
              <span className="metric-value">{availableSlotsCount}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">
                {t('publicSchedule.metrics.conflicts')}
              </span>
              <span className="metric-value">{busyIntervals.length}</span>
            </div>
          </PublicScheduleMetrics>

          <PublicScheduleSummary>
            <span className="summary-title">
              {t('publicSchedule.summary.title')}
            </span>
            <div className="summary-list">
              <div className="summary-item">
                <span className="summary-label">
                  {t('publicSchedule.summary.clinic')}
                </span>
                <span className="summary-value">
                  {loadedClinic ||
                    t('publicSchedule.summary.clinicPlaceholder')}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">
                  {t('publicSchedule.summary.professional')}
                </span>
                <span className="summary-value">
                  {selectedProfessional?.name ||
                    t('publicSchedule.summary.professionalPlaceholder')}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">
                  {t('publicSchedule.summary.date')}
                </span>
                <span className="summary-value">
                  {selectedDate
                    ? formatShortDate(selectedDate)
                    : t('publicSchedule.summary.datePlaceholder')}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">
                  {t('publicSchedule.summary.time')}
                </span>
                <span className="summary-value">
                  {selectedStartTime && computedEndTime
                    ? `${selectedStartTime} - ${computedEndTime}`
                    : t('publicSchedule.summary.timePlaceholder')}
                </span>
              </div>
            </div>
          </PublicScheduleSummary>
        </PublicScheduleHero>

        <PublicScheduleFormCard>
          {!submittedSchedule ? (
            <>
              <h2 className="card-title">{t('publicSchedule.card.title')}</h2>
              <p className="card-subtitle">
                {t('publicSchedule.card.subtitle')}
              </p>

              <Form
                form={form}
                layout="vertical"
                initialValues={{ duration_minutes: 30 }}
                onFinish={handleSubmit}
              >
                {!tenant && (
                  <>
                    <Form.Item
                      label={t('publicSchedule.clinic.label')}
                      name="clinic"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.clinic.required'),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t('publicSchedule.clinic.placeholder')}
                        prefix={<EnvironmentOutlined />}
                      />
                    </Form.Item>

                    <Button
                      type="default"
                      onClick={handleLoadClinic}
                      loading={loadingProfessionals}
                      style={{ marginBottom: 24 }}
                    >
                      {t('publicSchedule.clinic.loadProfessionals')}
                    </Button>
                  </>
                )}

                {loadedClinic &&
                professionals.length === 0 &&
                !loadingProfessionals ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                    message={t('publicSchedule.noProfessionals.title')}
                    description={t(
                      'publicSchedule.noProfessionals.description',
                    )}
                  />
                ) : null}

                <span className="section-title">
                  {t('publicSchedule.sections.patientData')}
                </span>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.patientName')}
                      name="patient_name"
                      rules={[
                        {
                          required: true,
                          message: t(
                            'publicSchedule.fields.patientNameRequired',
                          ),
                        },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder={t(
                          'publicSchedule.fields.patientNamePlaceholder',
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.document')}
                      name="cpf_cnpj"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.documentRequired'),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          'publicSchedule.fields.documentPlaceholder',
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.birthday')}
                      name="birthday"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.birthdayRequired'),
                        },
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                        inputReadOnly
                        placeholder={t(
                          'publicSchedule.fields.birthdayPlaceholder',
                        )}
                        maxDate={dayjs()}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.whatsapp')}
                      name="cell_phone"
                      getValueFromEvent={(event) =>
                        formatCellPhoneMask(event?.target?.value)
                      }
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.whatsappRequired'),
                        },
                        {
                          validator: async (_, value) => {
                            const digits = normalizeDigits(value);
                            if (digits.length === 10 || digits.length === 11) {
                              return;
                            }

                            throw new Error(
                              t('publicSchedule.fields.whatsappInvalid'),
                            );
                          },
                        },
                      ]}
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder={t(
                          'publicSchedule.fields.whatsappPlaceholder',
                        )}
                        inputMode="tel"
                        maxLength={15}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label={t('publicSchedule.fields.email')}
                  name="email"
                  rules={[
                    {
                      type: 'email',
                      message: t('publicSchedule.fields.emailInvalid'),
                    },
                  ]}
                >
                  <Input
                    placeholder={t('publicSchedule.fields.emailPlaceholder')}
                  />
                </Form.Item>

                <span className="section-title">
                  {t('publicSchedule.sections.appointment')}
                </span>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.professional')}
                      name="professional_id"
                      rules={[
                        {
                          required: true,
                          message: t(
                            'publicSchedule.fields.professionalRequired',
                          ),
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        loading={loadingProfessionals}
                        disabled={!loadedClinic || professionals.length === 0}
                        placeholder={t(
                          'publicSchedule.fields.professionalPlaceholder',
                        )}
                        optionFilterProp="label"
                        options={professionals.map((professional) => ({
                          label: professional.name,
                          value: professional.id,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.date')}
                      name="date"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.dateRequired'),
                        },
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                        inputReadOnly
                        placeholder={t('publicSchedule.fields.datePlaceholder')}
                        minDate={dayjs().startOf('day')}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="availability-card">
                  <span className="section-title">
                    {t('publicSchedule.availability.title')}
                  </span>
                  {loadingAvailability ? (
                    <Space align="center">
                      <Spin size="small" />
                      <Typography.Text>
                        {t('publicSchedule.availability.loading')}
                      </Typography.Text>
                    </Space>
                  ) : selectedProfessionalId && selectedDate ? (
                    busyIntervals.length > 0 ? (
                      <div className="availability-grid">
                        {busyIntervals.map((interval) => (
                          <div className="availability-item" key={interval.id}>
                            <span className="availability-time">
                              <CalendarOutlined />{' '}
                              {dayjs(interval.start).format('HH:mm')} -{' '}
                              {dayjs(interval.end).format('HH:mm')}
                            </span>
                            <div className="availability-meta">
                              {interval.title ||
                                t('publicSchedule.availability.reserved')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="availability-empty">
                        {t('publicSchedule.availability.empty')}
                      </div>
                    )
                  ) : (
                    <Typography.Text type="secondary">
                      {t('publicSchedule.availability.placeholder')}
                    </Typography.Text>
                  )}
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.startTime')}
                      name="start_time"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.startTimeRequired'),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t(
                          'publicSchedule.fields.professionalPlaceholder',
                        )}
                        suffixIcon={<ClockCircleOutlined />}
                        disabled={!selectedProfessionalId || !selectedDate}
                        options={timeOptionsWithAvailability}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('publicSchedule.fields.duration')}
                      name="duration_minutes"
                      rules={[
                        {
                          required: true,
                          message: t('publicSchedule.fields.durationRequired'),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t('publicSchedule.fields.datePlaceholder')}
                        suffixIcon={<FieldTimeOutlined />}
                        options={durationOptions}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label={t('publicSchedule.fields.observation')}
                  name="observation"
                >
                  <Input.TextArea
                    placeholder={t(
                      'publicSchedule.fields.observationPlaceholder',
                    )}
                  />
                </Form.Item>

                <Form.Item
                  name="privacy"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (value) return;
                        throw new Error(
                          t('publicSchedule.fields.privacyRequired'),
                        );
                      },
                    },
                  ]}
                >
                  <Checkbox>{t('publicSchedule.fields.privacy')}</Checkbox>
                </Form.Item>

                <Space
                  direction="vertical"
                  size={14}
                  style={{ width: '100%' }}
                  className="submit-section"
                >
                  <Alert
                    type={busyIntervals.length > 0 ? 'warning' : 'info'}
                    showIcon
                    message={t('publicSchedule.validation.title')}
                    description={
                      busyIntervals.length > 0
                        ? t('publicSchedule.validation.withConflicts')
                        : t('publicSchedule.validation.withoutConflicts')
                    }
                  />

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={submitting}
                    block
                    style={{ height: 48 }}
                  >
                    {t('publicSchedule.submit.button')}
                  </Button>
                </Space>
              </Form>
            </>
          ) : (
            <SuccessCard>
              <span className="success-pill">
                {t('publicSchedule.success.pill')}
              </span>
              <h2>{t('publicSchedule.success.title')}</h2>
              <p>{t('publicSchedule.success.description')}</p>

              <SuccessGrid>
                <div className="success-item">
                  <span className="success-label">
                    {t('publicSchedule.success.patient')}
                  </span>
                  <span className="success-value">
                    {submittedSchedule.patient_name}
                  </span>
                </div>
                <div className="success-item">
                  <span className="success-label">
                    {t('publicSchedule.success.professional')}
                  </span>
                  <span className="success-value">
                    {submittedSchedule.professional_name}
                  </span>
                </div>
                <div className="success-item">
                  <span className="success-label">
                    {t('publicSchedule.success.start')}
                  </span>
                  <span className="success-value">
                    {dayjs(submittedSchedule.start).format('DD/MM/YYYY HH:mm')}
                  </span>
                </div>
                <div className="success-item">
                  <span className="success-label">
                    {t('publicSchedule.success.status')}
                  </span>
                  <span className="success-value">
                    {submittedSchedule.status}
                  </span>
                </div>
              </SuccessGrid>

              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Alert
                  type="success"
                  showIcon
                  icon={<CheckCircleFilled />}
                  message={t('publicSchedule.success.nextStepTitle')}
                  description={t('publicSchedule.success.nextStepDescription')}
                />

                <Button
                  size="large"
                  onClick={() => {
                    setSubmittedSchedule(null);
                    form.resetFields();
                    form.setFieldValue('clinic', tenant || loadedClinic);
                  }}
                >
                  {t('publicSchedule.success.newRequest')}
                </Button>
              </Space>
            </SuccessCard>
          )}
        </PublicScheduleFormCard>
      </PublicScheduleShell>
    </PublicScheduleContainer>
  );
};

export default PublicSchedule;
